import React from 'react';

import Loader from '../loader';
import axios from '../axios';
import usePresignedImageUpload from '../../lib/use_presigned_image_upload';

const SectionImage = ({
  eventId,
  mainImageUrl,
  setMainImageUrl,
  subImages,
  setContents,
}) => {
  const [loadingMain, setLoadingMain] = React.useState(false);
  const [loadingSub1, setLoadingSub1] = React.useState(false);
  const [loadingSub2, setLoadingSub2] = React.useState(false);
  const [loadingSub3, setLoadingSub3] = React.useState(false);
  const [loadingSub4, setLoadingSub4] = React.useState(false);
  const [loadingBatch, setLoadingBatch] = React.useState(false);
  const { upload } = usePresignedImageUpload();
  const mainImageRef = React.useRef(null);
  const subImageRef1 = React.useRef(null);
  const subImageRef2 = React.useRef(null);
  const subImageRef3 = React.useRef(null);
  const subImageRef4 = React.useRef(null);
  const batchImageRef = React.useRef(null);

  const subImage1 = React.useMemo(() => {
    return subImages[0] || {};
  }, [subImages]); // ex: {id: 1, photo_url_medium: 'https://...'}
  const subImage2 = React.useMemo(() => {
    return subImages[1] || {};
  }, [subImages]);
  const subImage3 = React.useMemo(() => {
    return subImages[2] || {};
  }, [subImages]);
  const subImage4 = React.useMemo(() => {
    return subImages[3] || {};
  }, [subImages]);

  const updateSubImage = React.useCallback((subImageIndex) => {
    if (subImageIndex === 1) {
      subImageRef1.current.click();
    } else if (subImageIndex === 2) {
      subImageRef2.current.click();
    } else if (subImageIndex === 3) {
      subImageRef3.current.click();
    } else if (subImageIndex === 4) {
      subImageRef4.current.click();
    }
  }, []);

  const deleteSubImage = React.useCallback(
    (subImageId) => {
      axios
        .post('/api/event_edit/delete_content', {
          id: subImageId,
          event_id: eventId,
        })
        .then(() => {
          setContents((prev) =>
            prev.filter((content) => content.id !== subImageId)
          );
        })
        .catch(() => {
          alert('画像の削除に失敗しました。');
        });
    },
    [subImage1, subImage2, subImage3, subImage4]
  );

  const onChangeMainImage = React.useCallback(
    async (e) => {
      setLoadingMain(true);
      const file = e.target.files[0];
      if (!file) {
        setLoadingMain(false);
        return;
      }

      try {
        // Presigned URLでクラウドに直接アップロード
        const cacheData = await upload(file);
        // アップロードしたファイルの情報をDBに反映
        const res = await axios.post('/api/event/confirm_main_image_upload', {
          event_id: eventId,
          cache_data: cacheData,
        });

        setMainImageUrl(res.data.main_image_url);
      } catch (error) {
        alert(
          error?.response?.data?.error || '画像のアップロードに失敗しました。'
        );
      } finally {
        setLoadingMain(false);
      }
    },
    [eventId, upload]
  );

  const onChangeSubImage = React.useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) {
        return;
      }

      const contentId = e.target.name;
      const subImageIndex = Number(e.target.id);
      const setLoading = {
        1: setLoadingSub1,
        2: setLoadingSub2,
        3: setLoadingSub3,
        4: setLoadingSub4,
      }[subImageIndex];
      if (setLoading) setLoading(true);

      try {
        // Presigned URLでクラウドに直接アップロード
        const cacheData = await upload(file);
        // アップロードしたファイルの情報をDBに反映
        const res = await axios.post('/api/event_edit/confirm_sub_image_upload', {
          event_id: eventId,
          cache_data: cacheData,
          id: contentId,
        });

        setContents((prev) => {
          if (contentId) {
            return prev.map((content) => {
              if (content.id === res.data.id) {
                return res.data;
              } else {
                return content;
              }
            });
          } else {
            return [...prev, res.data];
          }
        });
      } catch {
        alert('画像のアップロードに失敗しました。');
      } finally {
        if (setLoading) setLoading(false);
      }
    },
    [eventId, upload]
  );

  // @context TGW-2195 「写真を追加」で既存のカバーを上書きせず、空いている枠へだけ追加する。
  const onChangeBatchImages = React.useCallback(
    async (e) => {
      const files = Array.from(e.target.files);
      e.target.value = '';
      if (!files.length) return;

      const hasMainImage = !!mainImageUrl;
      const availableCount = (hasMainImage ? 0 : 1) + (4 - subImages.length);
      if (files.length > availableCount) {
        alert('画像は最大5枚までです。');
        return;
      }

      const subLoadingSetters = [setLoadingSub1, setLoadingSub2, setLoadingSub3, setLoadingSub4];
      const activeSetters = [];
      const startsWithMainImage = !hasMainImage;
      const subFileCount = files.length - (startsWithMainImage ? 1 : 0);
      setLoadingBatch(true);
      if (startsWithMainImage) {
        setLoadingMain(true);
        activeSetters.push(setLoadingMain);
      }
      for (let i = 0; i < subFileCount; i++) {
        const setter = subLoadingSetters[subImages.length + i];
        setter(true);
        activeSetters.push(setter);
      }

      try {
        const cacheDataList = await Promise.all(files.map((file) => upload(file)));
        let subCacheDataList = cacheDataList;

        if (startsWithMainImage) {
          const mainRes = await axios.post('/api/event/confirm_main_image_upload', {
            event_id: eventId,
            cache_data: cacheDataList[0],
          });
          setMainImageUrl(mainRes.data.main_image_url);
          subCacheDataList = cacheDataList.slice(1);
        }

        const newContents = [];
        for (const cacheData of subCacheDataList) {
          const res = await axios.post('/api/event_edit/confirm_sub_image_upload', {
            event_id: eventId,
            cache_data: cacheData,
            id: '',
          });
          newContents.push(res.data);
        }
        if (newContents.length) {
          setContents((prev) => [...prev, ...newContents]);
        }
      } catch (error) {
        alert(
          error?.response?.data?.error || '画像のアップロードに失敗しました。'
        );
      } finally {
        setLoadingBatch(false);
        activeSetters.forEach((setter) => setter(false));
      }
    },
    [eventId, upload, mainImageUrl, subImages]
  );

  const totalImageCount = (mainImageUrl ? 1 : 0) + subImages.length;

  return (
    <section id="image">
      {/* @context TGW-2195 見出しをカードの外に置くと、下の YouTube カードまで「写真」の一部に見えるため、
          YouTube と同じく見出しと説明をカードの中に持たせる。 */}
      <div className="container col-md-8 col-lg-6 mt-3">
        <div className="shadow-sm mb-0 bg-white" style={{ borderRadius: 16, padding: '14px 16px' }}>
          <div style={{ marginBottom: (mainImageUrl || loadingMain) ? 12 : 8 }}>
            <p className="mb-1 d-flex align-items-center" style={{ gap: 8 }}>
              <span style={{ color: '#666', fontSize: 12, fontWeight: 800 }}>写真</span>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#8B9097', background: '#F0F0F0', borderRadius: 5, padding: '1px 6px' }}>任意</span>
            </p>
            <p className="text-gray text-fz-small mb-0">
              カバー画像と、イベントの様子が分かる写真を追加できます
            </p>
          </div>
          {(mainImageUrl || loadingMain) && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span style={{ color: '#666', fontSize: 12, fontWeight: 700 }}>カバー画像</span>
                <button
                  type="button"
                  onClick={() => mainImageRef.current.click()}
                  disabled={loadingMain || loadingBatch}
                  style={{ background: 'none', border: 'none', color: '#FF6467', fontSize: 13, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}
                >
                  画像を変更
                </button>
              </div>
              <div
                className="bg-gray"
                style={{ position: 'relative', width: '100%', paddingTop: '75%', borderRadius: 12, overflow: 'hidden' }}
              >
                {loadingMain ? (
                  <Loader isActive={loadingMain} />
                ) : (
                  <img
                    src={mainImageUrl}
                    alt="カバー画像"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingTop: mainImageUrl || loadingMain ? 12 : 0 }}>
            {loadingSub1 ? <ImageLoading /> : subImage1.photo_url_medium ? (
              <SubImage subImage={subImage1} updateSubImage={() => updateSubImage(1)} deleteSubImage={() => deleteSubImage(subImage1.id)} />
            ) : null}
            {loadingSub2 ? <ImageLoading /> : subImage2.photo_url_medium ? (
              <SubImage subImage={subImage2} updateSubImage={() => updateSubImage(2)} deleteSubImage={() => deleteSubImage(subImage2.id)} />
            ) : null}
            {loadingSub3 ? <ImageLoading /> : subImage3.photo_url_medium ? (
              <SubImage subImage={subImage3} updateSubImage={() => updateSubImage(3)} deleteSubImage={() => deleteSubImage(subImage3.id)} />
            ) : null}
            {loadingSub4 ? <ImageLoading /> : subImage4.photo_url_medium ? (
              <SubImage subImage={subImage4} updateSubImage={() => updateSubImage(4)} deleteSubImage={() => deleteSubImage(subImage4.id)} />
            ) : null}
          </div>

          <input type="file" accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif" ref={mainImageRef} onChange={onChangeMainImage} style={{ display: 'none' }} />
          <input id={1} name={subImage1.id} type="file" accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif" ref={subImageRef1} onChange={onChangeSubImage} style={{ display: 'none' }} />
          <input id={2} name={subImage2.id} type="file" accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif" ref={subImageRef2} onChange={onChangeSubImage} style={{ display: 'none' }} />
          <input id={3} name={subImage3.id} type="file" accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif" ref={subImageRef3} onChange={onChangeSubImage} style={{ display: 'none' }} />
          <input id={4} name={subImage4.id} type="file" accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif" ref={subImageRef4} onChange={onChangeSubImage} style={{ display: 'none' }} />

          {totalImageCount < 5 && (
            <div className="mt-2 mb-1">
              <button
                type="button"
                onClick={() => batchImageRef.current.click()}
                // @context TGW-2195 カバー単体のアップロード中に追加すると、バッチ側も1枚目をカバーとして送り競合する
                disabled={loadingBatch || loadingMain}
                style={{ background: 'none', border: 'none', color: '#FF6467', fontWeight: 700, fontSize: 13, padding: '8px 2px', cursor: 'pointer' }}
              >
                {loadingBatch ? 'アップロード中...' : '＋ 写真を追加（最大5枚）'}
              </button>
              <input
                type="file"
                accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                multiple
                ref={batchImageRef}
                onChange={onChangeBatchImages}
                style={{ display: 'none' }}
              />
            </div>
          )}
          <p className="text-gray text-fz-small mt-1 mb-0">
            カバー画像は横縦比4:3にすると、きれいに表示されます。
          </p>
        </div>
      </div>
    </section>
  );
};

const SubImage = ({ subImage, updateSubImage, deleteSubImage }) => (
  <div
    className="bg-gray"
    style={{ position: 'relative', width: 90, height: 90, borderRadius: 10, overflow: 'hidden', flex: 'none' }}
  >
    <button type="button" onClick={updateSubImage} style={{ border: 'none', padding: 0, width: '100%', height: '100%' }}>
      <img
        className="w-100 object-fit-cover adjust-box-inner"
        type="image"
        id={subImage.id}
        src={subImage.photo_url_medium}
        alt="追加写真"
      />
    </button>
    <button
      type="button"
      className="position-absolute bg-gray"
      aria-label="写真を削除"
      style={{ top: 0, right: 0, width: 24, height: 24, borderRadius: 12, border: 'none', padding: 0 }}
      onClick={deleteSubImage}
    >
      ×
    </button>
  </div>
);

// @context TGW-2195 空の写真枠は隠し、アップロード中の枠だけ進捗を表示する。
const ImageLoading = () => (
  <div
    className="bg-gray"
    style={{ position: 'relative', width: 90, height: 90, borderRadius: 10, overflow: 'hidden', flex: 'none' }}
  >
    <Loader isActive />
  </div>
);

export default SectionImage;
